import { useReservationStore, type TermsChecked } from '@/stores/reservation-store';

type TermKey = keyof TermsChecked;

type TermLineSegment = {
  text: string;
  emphasized?: boolean;
};

type TermLine = {
  segments: TermLineSegment[];
  bulleted?: boolean;
};

type TermSection = {
  key: TermKey;
  title: string;
  lines: TermLine[];
};

const termSections: TermSection[] = [
  {
    key: 'reservationConfirmation',
    title: '예약 확정 안내',
    lines: [
      { segments: [{ text: '보다 원활한 운영과 노쇼 방지를 위해 선입금 제도로 운영되고 있습니다.' }], bulleted: false },
      {
        segments: [
          { text: '예약은 ' },
          { text: '입금 완료 시', emphasized: true },
          { text: ' 최종 확정됩니다.' },
        ],
      },
      {
        segments: [
          { text: '예약 후 ' },
          { text: '1시간 이내 미입금', emphasized: true },
          { text: ' 시 자동 취소됩니다.' },
        ],
      },
      { segments: [{ text: '예약번호로 현금영수증이 자동 발급됩니다.' }] },
      { segments: [{ text: '결제 수단 변경은 요청사항에 남겨주시면 예약 당일 현장에서 도와드립니다.' }] },
    ],
  },
  {
    key: 'reservationChange',
    title: '예약 변경 안내',
    lines: [
      { segments: [{ text: '예약 변경 시 여러 건의 예약을 진행하지 마시고, 반드시 문의 부탁드립니다.' }] },
      { segments: [{ text: '중복 예약은 다른 손님의 예약을 제한할 수 있습니다.' }] },
    ],
  },
  {
    key: 'cancellationRefund',
    title: '취소 및 환불',
    lines: [
      {
        segments: [
          { text: '예약 취소 및 환불은 이용일 기준 ' },
          { text: '1일 전', emphasized: true },
          { text: '까지 가능합니다.' },
        ],
      },
      {
        segments: [
          { text: '당일 취소 및 노쇼', emphasized: true },
          { text: '의 경우 환불이 어렵습니다.' },
        ],
      },
    ],
  },
  {
    key: 'usageGuide',
    title: '이용 안내',
    lines: [
      {
        segments: [
          { text: '매장 예약은 좌석 예약이 아닌 ' },
          { text: '빵 예약', emphasized: true },
          { text: '입니다.' },
        ],
      },
      { segments: [{ text: '매장 좌석이 만석일 경우 웨이팅이 발생할 수 있습니다.' }] },
      {
        segments: [
          {
            text: '일부 제품은 정해진 시간 이후에만 수령 가능하며, 해당 시간 이전 방문 시 예약 여부와 관계없이 제공이 어렵습니다.',
          },
        ],
      },
      { segments: [{ text: '예약하신 시간에 맞춰 제품이 준비되며, 이른 방문 시 수령이 어렵거나 대기 시간이 발생할 수 있습니다.' }] },
    ],
  },
];

export default function TermsChecklist() {
  const { termsChecked, updateTermsChecked } = useReservationStore();

  const handleCheckChange = (field: TermKey) => {
    updateTermsChecked({ [field]: !termsChecked[field] });
  };

  return (
    <div className="px-5 pt-10 pb-6">
      <div className="pb-5">
        <h2 className="text-xl font-semibold text-[#1F1F1F]">유의사항</h2>
      </div>

      <div className="flex flex-col gap-8">
        {termSections.map(section => (
          <button
            key={section.key}
            type="button"
            aria-pressed={termsChecked[section.key]}
            onClick={() => handleCheckChange(section.key)}
            className="space-y-3 rounded-md text-left transition-colors duration-150"
          >
            <div className="rounded-[10px] bg-[#F1F3F5] px-4 py-5">
              <h3 className="mb-1 text-base font-bold leading-[1.6] text-[#454545]">[{section.title}]</h3>
              <ul className="space-y-0.5 text-base font-medium leading-[1.6] text-[#454545]">
                {section.lines.map((line, index) => (
                  <li key={index} className="flex gap-1">
                    {line.bulleted === false ? null : <span className="shrink-0">•</span>}
                    <span>
                      {line.segments.map((segment, segmentIndex) => (
                        <span key={segmentIndex} className={segment.emphasized ? 'font-bold text-[#FF5A5A]' : undefined}>
                          {segment.text}
                        </span>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex w-full items-center gap-3 rounded-md py-1">
              <img
                src={termsChecked[section.key] ? '/assets/checked.svg' : '/assets/unchecked.svg'}
                alt={termsChecked[section.key] ? '체크됨' : '체크 안됨'}
                width={20}
                height={20}
              />
              <span className="text-base font-semibold text-[#1F1F1F]">네, 확인했어요</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
